import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DashboardProject, Project } from '../../project/types/project';
import { ProjectFilters } from '../components/ProjectFilters/ProjectFilters';
import { ProjectGrid } from '../components/ProjectGrid/ProjectGrid';
import { useHomePageState } from '../hooks/useHomePageState';
import ProjectCreationForm from '../components/ProjectCreationForm';
import type { HomePageProps } from '../types/home';
import DashedBoard from '../../project/components/dashboard/Index';
import { useCurrentProject } from '../../../redux/WeldData/hooks/useWeldData.js';
import { useUserPermissionsAfterLogin } from '../../admin/hooks/useUserPermissionsAfterLogin';

export const HomePage: React.FC<HomePageProps> = ({
  showCreateForm,
  onCreateFormClose,
  setSubtitle,
  setShowBackNavigation,
  showBackNavigation,
  isEditProject,
  urlProjectId,
  urlTab,
}) => {
  const navigate = useNavigate();
  const { setProject } = useCurrentProject();
  const { hasPermission } = useUserPermissionsAfterLogin();
  const {
    searchQuery,
    filters,
    projects,
    filteredProjects,
    hasActiveFilters,
    setSearchQuery,
    setFilters,
    resetFilters,
    uniqueCustomers,
    loadProjects,
    isProjectListLoading,
  } = useHomePageState({
    onProjectSelect: () => {}, // navigation drives selection; hook's setter is unused now
  });

  // Derive selectedProject from URL :projectId — synchronously, so there is no
  // brief render where the list flashes after clicking into a project.
  // Compare as strings because URL params are strings while project ids may be numeric.
  const selectedProject: DashboardProject | null = useMemo(() => {
    if (!urlProjectId) return null;
    const project = filteredProjects.find((p: Project) => String(p.projectId) === urlProjectId);
    if (!project) return null;
    return { project, team: [], documents: [], materials: [] };
  }, [urlProjectId, filteredProjects]);

  // If the URL references a project id that isn't in the loaded list, bounce to the list.
  useEffect(() => {
    if (!urlProjectId) return;
    if (filteredProjects.length === 0) return; // wait for fetch
    const exists = filteredProjects.some((p: Project) => String(p.projectId) === urlProjectId);
    if (!exists) navigate('/Projects', { replace: true });
  }, [urlProjectId, filteredProjects, navigate]);

  // Set the current project in the WeldData store when a project is selected
  useEffect(() => {
    if (selectedProject) {
      const settings = localStorage.getItem('settings');
      if(settings){
        const parsedSettings = JSON.parse(settings);
        parsedSettings.general.unit = selectedProject.project.unit ==='in' ? 'Imperial' : 'Metric';
        localStorage.setItem('settings', JSON.stringify(parsedSettings));
      }
      else {
        localStorage.setItem('settings', JSON.stringify({ general: { unit: selectedProject.project.unit ==='in' ? 'Imperial' : 'Metric'}}));
      }
      setProject(selectedProject);
    }
  }, [selectedProject, setProject]);

  // Clear subtitle when projects are loading/reloading
  useEffect(() => {
    if (isProjectListLoading) {
      setSubtitle('');
    }
  }, [isProjectListLoading, setSubtitle]);

  // Set subtitle when a project is selected (only when not loading)
  useEffect(() => {
    if (selectedProject && showBackNavigation && !isProjectListLoading) {
      setSubtitle(selectedProject.project.projectName);
    } else if (!selectedProject) {
      setSubtitle('');
    }
  }, [selectedProject, showBackNavigation, isProjectListLoading, setSubtitle]);

  const handleBack = () => {
    // Closing the edit form returns to the dashboard; selectedProject is derived
    // from the URL, so nothing else to restore.
    onCreateFormClose();
  }

  const handleEditSubmit = () => {
    // After successful edit submission, the useEffect will automatically update the selected project
    // when filteredProjects is refreshed from the backend
    onCreateFormClose();
  }

  const isDetailView = !!selectedProject && showBackNavigation && !showCreateForm;
  const stretchClass = isDetailView ? 'flex-1 flex flex-col min-h-0' : '';
  return (
    <div className={`w-full mx-auto ${stretchClass}`}>
      {hasPermission('view_projects') && <div className={`w-full mx-auto ${stretchClass}`}>
        {!showCreateForm &&
          <>

            {selectedProject && showBackNavigation ? (
              <>
                <DashedBoard
                  project={selectedProject}
                  onBack={() => {
                    setSubtitle('');
                    navigate('/Projects');
                  }}
                  urlTab={urlTab}
                />
              </>
            ) : (
              <>
                <>

                  {projects.length > 0 && <ProjectFilters
                    searchQuery={searchQuery}
                    onSearchChange={setSearchQuery}
                    filters={{
                      status: filters.status || [],
                      type: filters.type || [],
                      customer: filters.customer || []
                    }}
                    onFilterChange={(key) => (value) => setFilters(prev => ({ ...prev, [key]: value }))}
                    uniqueCustomers={uniqueCustomers}
                    hasActiveFilters={hasActiveFilters}
                    filteredCount={filteredProjects.length}
                    onResetFilters={resetFilters}
                  />}

                   <ProjectGrid
                    projects={filteredProjects}
                    onProjectClick={(project) => navigate(`/Projects/${project.projectId}`)}
                    setShowBackNavigation={setShowBackNavigation}
                    isProjectsLoading={isProjectListLoading}
                    hasActiveFilters={hasActiveFilters}
                    originalProjects={projects}
                    fetchProjectList={loadProjects}
                  />
                </>
              </>
            )}
          </>}

        {showCreateForm && <ProjectCreationForm onBack={handleBack} onEditSubmit={handleEditSubmit} isEditProject={isEditProject} projectData={selectedProject?.project} />}
      </div>
      }
    </div>
  );
}; 